import React from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Modal, Row } from 'react-bootstrap-5';

import PropertyTypeInput from './inputs/property_type_input';
import RangeInput from './inputs/range_input';
import DateRangeInput from './inputs/date_range_input';
import RegionInput from './inputs/region_input';
import TextInput from './inputs/text_input';
import Select from './inputs/select';
import BooleanSelect from './inputs/boolean_select';
import StateSelect from './inputs/state_select';
import SingleCheck from './inputs/single_check';

import { ColumnHeader, Filter } from './modal_components';

import { AIR_CONDITIONING_CODES, BUILDING_CONDITIONS } from './constants';
import BuildingClassInput from './inputs/building_class_input';

function OffMarketFiltersModal({
  hasProspecting,
  show,
  userType,
  onHide
}) {
  return (
    <Modal className="search-filters" fullscreen="sm-down" scrollable={true} show={show} size="xl" onHide={onHide}>
      <Modal.Header className="border-0" closeButton>
        <Modal.Title as="h5">All Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-white p-0">
        <Row className="gx-0">
          <Col className="border-end" md={6} xs={12} xxl={hasProspecting && 4}>
            <ColumnHeader>
              Property Information
            </ColumnHeader>
            {(hasProspecting || userType !== 'broker') && (
              <div className="my-4 px-5">
                {hasProspecting && <SingleCheck attribute="bookmarked" label="Bookmarked" type="switch" />}
                {userType !== 'broker' && (
                  <SingleCheck attribute="watchlisted" label="Watchlisted" type="switch" />
                )}
                {hasProspecting && <SingleCheck attribute="qoz" label="Opportunity Zone" type="switch"/>}
                <SingleCheck attribute="claimed" label="Claimed by Me" type="switch"/>
              </div>
            )}
            {hasProspecting && userType === 'broker' && (
              <Filter title="Demand Data">
                <SingleCheck attribute="interestedInvestors" label="Has Interested Buyers" />
              </Filter>
            )}
            <Filter bordered title="Property Type">
              <PropertyTypeInput />
            </Filter>
            <Filter title="Region">
              <RegionInput />
            </Filter>
            <Filter title="Zoning">
              <TextInput attribute="zoning" placeholder="Enter a zoning code" />
            </Filter>
            <Filter title="Assessor Parcel Number (APN)">
              <TextInput attribute="apn" placeholder="Enter an APN" />
            </Filter>
            <Filter title="Air Conditioning Code">
              <Select
                attribute="airConditioningId"
                includeBlank={true}
                options={AIR_CONDITIONING_CODES.map(i => ({ label: i, value: i }))}
              />
            </Filter>
            <Filter title="Building Condition">
              <Select
                attribute="buildingCondition"
                includeBlank={true}
                options={BUILDING_CONDITIONS.map(i => ({ label: i, value: i }))}
              />
            </Filter>
          </Col>
          <Col className="border-end" md={6} xs={12} xxl={hasProspecting && 4}>
            <ColumnHeader>
              Building Details
            </ColumnHeader>
            <Filter title="Building SF">
              <RangeInput
                maxAttribute="maxBuildingSizeSf"
                minAttribute="minBuildingSizeSf"
                placeholder="SF"
              />
            </Filter>
            <Filter title="Number of Units">
              <RangeInput
                maxAttribute="maxNumberOfUnits"
                minAttribute="minNumberOfUnits"
                placeholder=""
              />
            </Filter>
            <Filter title="Number of Residential Units">
              <RangeInput
                maxAttribute="maxResidentialUnits"
                minAttribute="minResidentialUnits"
                placeholder=""
              />
            </Filter>
            <Filter title="Number of Commercial Units">
              <RangeInput
                maxAttribute="maxCommercialUnits"
                minAttribute="minCommercialUnits"
                placeholder=""
              />
            </Filter>
            <Filter title="Number of Buildings">
              <RangeInput
                maxAttribute="maxNumberOfBuildings"
                minAttribute="minNumberOfBuildings"
                placeholder=""
              />
            </Filter>
            <Filter title="Number of Stories">
              <RangeInput
                maxAttribute="maxNumberOfStories"
                minAttribute="minNumberOfStories"
                placeholder=""
              />
            </Filter>
            <Filter title="Floor Area Ratio">
              <RangeInput
                maxAttribute="maxFloorAreaRatio"
                minAttribute="minFloorAreaRatio"
                placeholder=""
              />
            </Filter>
            <Filter title="Year Built">
              <RangeInput
                maxAttribute="maxYearBuilt"
                minAttribute="minYearBuilt"
                placeholder=""
              />
            </Filter>
            <Filter title="Year Renovated">
              <RangeInput
                maxAttribute="maxYearRenovated"
                minAttribute="minYearRenovated"
                placeholder=""
              />
            </Filter>
            <Filter bordered title="Building Class">
              <BuildingClassInput />
            </Filter>
            <ColumnHeader>
              Lot Information
            </ColumnHeader>
            <Filter title="Lot Size">
              <RangeInput
                maxAttribute="maxLotSizeAcres"
                minAttribute="minLotSizeAcres"
                placeholder="Acres"
              />
            </Filter>
            <Filter title="Lot Depth">
              <RangeInput
                maxAttribute="maxLotDepth"
                minAttribute="minLotDepth"
                placeholder="Ft"
              />
            </Filter>
            <Filter title="Lot Frontage">
              <RangeInput
                maxAttribute="maxLotFrontage"
                minAttribute="minLotFrontage"
                placeholder="Ft"
              />
            </Filter>
          </Col>
          {hasProspecting && (
            <Col md={6} xs={12} xxl={4}>
              <ColumnHeader>
                Owner Information
              </ColumnHeader>
              <Filter title="Corporate Owned">
                <BooleanSelect attribute="corporateOwned" includeBlank={true} />
              </Filter>
              <Filter title="Occupied by Owner">
                <BooleanSelect attribute="ownerOccupied" includeBlank={true} />
              </Filter>
              <Filter title="Owner City">
                <TextInput attribute="ownerCity" placeholder="Enter the owner's city" />
              </Filter>
              <Filter title="Owner State">
                <StateSelect />
              </Filter>
              <Filter title="Owner Zip">
                <TextInput
                  attribute="ownerZip"
                  placeholder="Enter the owner's zip code"
                />
              </Filter>
              <ColumnHeader>
                Financial Information
              </ColumnHeader>
              <Filter title="Total Assessed Value">
                <RangeInput
                  maxAttribute="maxTotalAssessedValue"
                  minAttribute="minTotalAssessedValue"
                  placeholder=""
                />
              </Filter>
              <Filter title="Last Transaction Amount">
                <RangeInput
                  maxAttribute="maxLastTransactionAmount"
                  minAttribute="minLastTransactionAmount"
                  placeholder=""
                />
              </Filter>
              <Filter title="Last Transaction Amount per SF">
                <RangeInput
                  maxAttribute="maxLastTransactionAmountPerSf"
                  minAttribute="minLastTransactionAmountPerSf"
                  placeholder=""
                />
              </Filter>
              <Filter title="Last Sale Date">
                <DateRangeInput
                  maxAttribute="maxTransactionDate"
                  minAttribute="minTransactionDate"
                  placeholder=""
                />
              </Filter>
              <Filter title="Loan Starting Date">
                <DateRangeInput
                  maxAttribute="maxLoanBeginningDate"
                  minAttribute="minLoanBeginningDate"
                  placeholder=""
                />
              </Filter>
              <Filter title="Loan Maturity Date">
                <DateRangeInput
                  maxAttribute="maxLoanMaturityDate"
                  minAttribute="minLoanMaturityDate"
                  placeholder=""
                />
              </Filter>
              <Filter title="Transaction Date">
                <div className="d-flex align-items-center gap-2">
                  <TextInput attribute="transactionDay" placeholder="Day" type="number" />
                  <TextInput attribute="transactionMonth" placeholder="Month" type="number" />
                  <TextInput attribute="transactionYear" placeholder="Year" type="number" />
                </div>
              </Filter>
            </Col>
          )}
        </Row>
      </Modal.Body>
      <Modal.Footer className="py-0">
        <Button variant="primary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

OffMarketFiltersModal.propTypes = {
  hasProspecting: PropTypes.bool,
  show: PropTypes.bool.isRequired,
  userType: PropTypes.string,
  onHide: PropTypes.func.isRequired
};

export default OffMarketFiltersModal;
